/**
 * Dev-only status overrides, so the requirement colours can be seen without contriving real data.
 *
 * <p>Most states are hard to reach on a dev machine: "in place" needs a real upload, findings need an
 * assessor that reports something, and "checking" barely exists at all while assessment is synchronous.
 * That left three of the palette's states effectively unreviewable.</p>
 *
 * <p><b>This is a DISPLAY override and nothing else.</b> It rewrites the rows on their way to the
 * screen and never touches the API, so it cannot write a compliance state into storage — which is the
 * whole reason it works this way rather than as a dev endpoint that stamps a document. Nothing here
 * can make the product believe a company holds a document it does not (COMPLIANCE-MODEL.md §6). It
 * lives in sessionStorage, so it dies with the tab.</p>
 */

import type {
  ChecklistCategory,
  ChecklistDocumentDetail,
  ChecklistFinding,
  ChecklistItem,
  ChecklistSummary,
  ComplianceScore,
  TierProgress,
} from '../lib/entities/checklist';

/** Same gate as the onboarding shortcut: dev build AND localhost. */
export const canDevOverride = (): boolean =>
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

export type DevState =
  | 'missing'
  | 'checking'
  | 'critical'
  | 'important'
  | 'suggestion'
  | 'clean'
  | 'problem';

/** The buttons, in the order they appear. Labels say the STATE, not the colour. */
export const DEV_STATES: { key: DevState; label: string }[] = [
  { key: 'missing', label: 'Missing' },
  { key: 'checking', label: 'Checking' },
  { key: 'critical', label: 'Critical finding' },
  { key: 'important', label: 'Important finding' },
  { key: 'suggestion', label: 'Suggestion' },
  { key: 'clean', label: 'In place' },
  { key: 'problem', label: 'Should not have' },
];

const KEY = 'reeve.dev.statusOverrides';

type Overrides = Record<string, DevState>;

function read(): Overrides {
  if (!canDevOverride()) return {};
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || '{}') as Overrides;
  } catch {
    return {};
  }
}

function write(next: Overrides) {
  sessionStorage.setItem(KEY, JSON.stringify(next));
  // Same-tab storage events don't fire, so tell any mounted list to re-read.
  window.dispatchEvent(new Event('reeve-dev-override'));
}

export const overrideFor = (typeId: string): DevState | null => read()[typeId] ?? null;

export function setOverride(typeId: string, state: DevState | null) {
  const next = read();
  if (state) next[typeId] = state;
  else delete next[typeId];
  write(next);
}

export function clearOverrides() {
  write({});
}

export const hasOverrides = (): boolean => Object.keys(read()).length > 0;

/** A plausible finding, so the detail page's fix list has something well-formed to render. */
function fakeFinding(i: number, severity: ChecklistFinding['severity']): ChecklistFinding {
  return {
    id: `dev-${i}`,
    severity,
    title: 'Example finding',
    text: 'This is a placeholder finding from the dev status override, so the page has something to lay out.',
    action: 'Nothing to do. Clear the override to remove it.',
    basis: null,
    anchor: null,
    fix: null,
    fixLabel: null,
  };
}

const FINDINGS: Record<string, { severity: ChecklistFinding['severity']; count: number }> = {
  critical: { severity: 'critical', count: 3 },
  important: { severity: 'important', count: 2 },
  suggestion: { severity: 'suggestion', count: 1 },
};

/**
 * The shared shape of an overridden row. Status, health and finding counts are set together, because
 * they are what the palette and the pills read, and a half-applied combination would show a state the
 * real resolver can never produce.
 */
function shape(state: DevState) {
  const findings = FINDINGS[state];
  if (findings) {
    return {
      status: 'present' as const,
      has: true,
      health: 'hasFindings' as const,
      topSeverity: findings.severity,
      findingCount: findings.count,
    };
  }
  switch (state) {
    case 'checking':
      return { status: 'present' as const, has: true, health: 'unchecked' as const, topSeverity: null, findingCount: 0 };
    case 'clean':
      return { status: 'present' as const, has: true, health: 'clean' as const, topSeverity: null, findingCount: 0 };
    case 'problem':
      return { status: 'shouldNotHave' as const, has: true, health: 'clean' as const, topSeverity: null, findingCount: 0 };
    case 'missing':
    default:
      return { status: 'missing' as const, has: false, health: 'notApplicable' as const, topSeverity: null, findingCount: 0 };
  }
}

/**
 * A stand-in id for a held document, so a previewed "in place" card renders its Download action and
 * the layout matches the real thing. The bytes behind it do not exist: opening the picker is fine,
 * choosing a format will fail with the usual inline error. That is the honest cost of previewing a
 * state without inventing a file, and it only bites if you actually try to download the placeholder.
 */
const FAKE_DOC_ID = '00000000-0000-4000-8000-0000dev00001';

/** Apply any override to a checklist row. Identity when there is none, or outside dev. */
export function applyOverride(item: ChecklistItem): ChecklistItem {
  const state = overrideFor(item.id);
  if (!state) return item;

  const s = shape(state);
  const held = s.status === 'present';
  return {
    ...item,
    ...s,
    documentId: held ? item.documentId ?? FAKE_DOC_ID : item.documentId,
    fileName: held ? item.fileName ?? `${item.id}.docx` : item.fileName,
  };
}

/**
 * Re-score an overridden checklist, so the header percentage responds to the dev buttons.
 *
 * <p><b>This MIRRORS Reeve.Core/Compliance/ComplianceScoring.cs and must be kept in step with it.</b>
 * The real score is computed on the backend, deliberately, so this page and the dashboard cannot
 * disagree and so the rules are covered by ComplianceScoringTests. But the backend scores the REAL
 * data, which no override touches — leaving the one number the dev tool exists to help evaluate as the
 * only thing on the page that would not move.</p>
 *
 * <p>Only ever called when overrides are active, and the header says so on screen, so a wrong number
 * here can never be mistaken for the product's own.</p>
 */
const WEIGHT: Record<ChecklistCategory, number> = {
  legallyRequired: 1,
  essential: 0.5,
  bestPractice: 0.25,
  optional: 0,
  notRelevant: 0,
  shouldNotHave: 0,
};
// Half for having the document, half for it being right — so unread and findings-found score the same,
// and completing a check can only raise the number or leave it.
const FINDINGS_FACTOR = 0.5;
const UNCHECKED_FACTOR = 0.5;
const PROBLEM_PENALTY = 1;

export function rescore(items: ChecklistItem[]): Pick<ChecklistSummary, 'score' | 'tiers'> {
  let earned = 0;
  let possible = 0;
  let penalty = 0;
  let unchecked = 0;
  let withFindings = 0;
  let problems = 0;

  for (const i of items) {
    if (i.status === 'shouldNotHave') {
      if (i.has) {
        problems += 1;
        penalty += PROBLEM_PENALTY;
      }
      continue;
    }
    const weight = i.category ? WEIGHT[i.category] : 0;
    if (weight <= 0) continue;

    possible += weight;
    if (i.status !== 'present') continue;

    if (i.health === 'hasFindings') {
      withFindings += 1;
      earned += weight * FINDINGS_FACTOR;
    } else if (i.health === 'unchecked') {
      unchecked += 1;
      earned += weight * UNCHECKED_FACTOR;
    } else {
      earned += weight;
    }
  }

  const net = Math.max(0, earned - penalty);
  const score: ComplianceScore = {
    earned: net,
    possible,
    percent: possible <= 0 ? 100 : Math.round((net / possible) * 100),
    unchecked,
    withFindings,
    problems,
    penalty,
  };

  const tiers: TierProgress[] = (
    ['legallyRequired', 'essential', 'bestPractice', 'optional'] as ChecklistCategory[]
  ).map((tier) => {
    const inTier = items.filter((i) => i.category === tier && i.needed);
    return {
      tier,
      needed: inTier.length,
      present: inTier.filter((i) => i.status === 'present').length,
      withFindings: inTier.filter((i) => i.health === 'hasFindings').length,
    };
  });

  return { score, tiers };
}

/** Apply any override to the document detail, including synthesised findings for the fix list. */
export function applyOverrideToDetail(detail: ChecklistDocumentDetail): ChecklistDocumentDetail {
  const state = overrideFor(detail.id);
  if (!state) return detail;

  const s = shape(state);
  const spec = FINDINGS[state];
  return {
    ...detail,
    status: s.status,
    health: s.health,
    findings: spec
      ? Array.from({ length: spec.count }, (_, i) => fakeFinding(i, spec.severity))
      : [],
    // Deliberately NOT faking an instance: the detail page would try to fetch its bytes and fail. The
    // status box, its colour and the fix list are what this is for.
    instance: detail.instance,
  };
}
